import {
  APP_NAME,
  CONSENT_TEXT,
  JOIN_BUTTON,
  JOIN_PATH,
  LINK_PATH,
  type MessageContext,
  OPERATOR,
  PRODUCTION_ORIGIN,
  REQUEST_BUTTON,
  SUPPORT_CONTACT,
  templates,
} from '@eventping/shared'
import { Link } from '@tanstack/react-router'
import type * as React from 'react'
import { Card, Footer, Page, Wordmark } from '@/components/shell'
import { useMe } from '@/lib/api'

/**
 * The public proof of opt-in for toll-free verification (https://eventping.app/opt-in): how
 * people join, real screenshots of the join form and the confirmation text (generated from the
 * app by apps/web/e2e/proof.spec.ts into apps/web/public/proof), the exact consent text and
 * every message, rendered from the same templates the server sends.
 */

const SAMPLE_LINK = `${PRODUCTION_ORIGIN}${LINK_PATH}Ab3dE7hJk9mQ`
const SAMPLE_JOIN = `${PRODUCTION_ORIGIN.replace('https://', '')}${JOIN_PATH}Xk7mP2qa`

function Shot({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="space-y-2">
      <img
        src={src}
        alt={alt}
        width={390}
        className="mx-auto w-full max-w-[300px] rounded-2xl border shadow-sm"
        loading="lazy"
      />
      <figcaption className="text-center text-sm text-muted-foreground">{caption}</figcaption>
    </figure>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <span
        className="grid size-9 shrink-0 place-items-center rounded-full bg-primary font-semibold text-primary-foreground"
        aria-hidden
      >
        {n}
      </span>
      <div className="min-w-0 space-y-2 pt-1">
        <h3 className="text-lg font-semibold">{title}</h3>
        {children}
      </div>
    </li>
  )
}

export function OptInPage() {
  const me = useMe().data
  const ctx: MessageContext = {
    appName: APP_NAME,
    origin: PRODUCTION_ORIGIN,
    support: me?.support ?? SUPPORT_CONTACT,
    confirmStyle: me?.confirmStyle ?? 'long',
  }
  const messages: { when: string; text: string }[] = [
    {
      when: 'Opt-in confirmation (sent once, right after someone signs up)',
      text: templates.confirm(ctx, { event: 'House move', link: SAMPLE_LINK }),
    },
    {
      when: 'Step update (when the organizer marks a step done)',
      text: templates.step(ctx, {
        prefix: '',
        event: 'House move',
        step: 'Truck loaded',
        note: 'On our way',
        link: SAMPLE_LINK,
      }),
    },
    { when: 'Reply to HELP', text: templates.help(ctx) },
    { when: 'Reply to STOP', text: templates.stop(ctx) },
  ]

  return (
    <Page
      header={
        <Link to="/" aria-label={`${APP_NAME} home`}>
          <Wordmark />
        </Link>
      }
    >
      <article className="space-y-6">
        <header>
          <p className="text-sm font-medium text-primary">{APP_NAME} text messaging</p>
          <h1 className="mt-1 text-3xl font-semibold text-balance">How people opt in</h1>
          <p className="mt-3 text-lg text-muted-foreground">
            {APP_NAME} never texts anyone first. Every number we text was typed in by its owner on a{' '}
            {APP_NAME} join page, next to a consent checkbox they ticked themselves. Organizers
            can’t sign anyone else up.
          </p>
          <p className="mt-3 text-lg" data-testid="operator">
            The sender is {APP_NAME}. The sign-up confirmation and every HELP, STOP and START reply
            start with “{APP_NAME}:”; step updates start with the event’s name and link to its page.{' '}
            {APP_NAME} is operated by {OPERATOR}. Contact: {ctx.support}.
          </p>
          <p className="mt-3 text-lg">
            Try the live form:{' '}
            <a href="/join/demo" className="font-medium text-primary underline underline-offset-4">
              the demo join page
            </a>{' '}
            ({PRODUCTION_ORIGIN}/join/demo), the real join form for a sample event. It stores and
            sends nothing.
          </p>
        </header>

        <Card>
          <h2 className="mb-5 text-xl font-semibold">Step by step</h2>
          <ol className="space-y-8">
            <Step n={1} title="The organizer shares a join link">
              <p>
                Every event has its own join link, like <code>{SAMPLE_JOIN}</code>, which the
                organizer shares with the people who want updates. They can replace it or turn it
                off at any time. An organizer can also add someone by name and copy that person’s
                personal join link to send themselves. Adding someone never sends them a text.
              </p>
            </Step>
            <Step n={2} title="The person fills in the form">
              <p>
                The join page names the event and who it’s from. The person enters their name and an
                email address (required) and, if they want texts, their mobile number (optional). As
                soon as they enter a number, the SMS consent checkbox appears,{' '}
                <strong>unchecked</strong>. They must tick it to get texts; the form won’t submit a
                number without it. Email updates need no checkbox, and every email has a one-click
                unsubscribe link. See it live on the{' '}
                <a href="/join/demo" className="font-medium underline">
                  demo join page
                </a>
                .
              </p>
              <div className="grid gap-6 pt-2 sm:grid-cols-2">
                <Shot
                  src="/proof/join-form.png"
                  alt="The EventPing join page for the event House move: name and mobile number fields, an unchecked consent checkbox with the consent text, and the button Yes, send me updates."
                  caption="The join form, as it opens: the box is unchecked."
                />
                <Shot
                  src="/proof/join-form-filled.png"
                  alt="The same join page filled in with a sample name and a 555 number, with the consent checkbox ticked."
                  caption="Filled in, with the box ticked by the person."
                />
              </div>
            </Step>
            <Step n={3} title={`They tap “${JOIN_BUTTON}”`}>
              <p>
                We record the consent: the time, the event, the number, the exact consent text and
                its version, a one-way hash of the device’s IP address, and the browser’s user
                agent.
              </p>
              <p>
                Most events are private: there the button reads “{REQUEST_BUTTON}”, and the
                organizer approves each request first. The consent is recorded when it’s given, but
                nothing at all is sent until the organizer approves. A denied request gets no text.
              </p>
              <div className="grid gap-6 pt-2 sm:grid-cols-2">
                <Shot
                  src="/proof/request-form.png"
                  alt="The request form for a private event: name, email, mobile number, the ticked SMS consent checkbox, and the button Request updates."
                  caption="A private event: the same consent box, and a request."
                />
                <Shot
                  src="/proof/join-success.png"
                  alt="The EventPing page after joining: You’ll get updates, House move, with a note that a confirmation text was sent."
                  caption="What they see next."
                />
              </div>
            </Step>
            <Step n={4} title="One confirmation text">
              <p>
                {APP_NAME} sends a single confirmation with the program name, how often messages
                come, and how to get help or stop: right away on a public event, or when the
                organizer approves the request on a private one. After that, they only get a text
                when the organizer marks a step of that event done.
              </p>
              <div className="pt-2">
                <Shot
                  src="/proof/confirmation-text.png"
                  alt="The confirmation text as received: EventPing: You're signed up for updates on House move. Msg freq varies. Reply STOP to opt out, HELP for help, followed by a link."
                  caption="The confirmation text, as received."
                />
              </div>
            </Step>
          </ol>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold">The consent text</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Shown next to the unchecked checkbox on every join page once a mobile number is entered,
            with links to our Terms and Privacy Policy:
          </p>
          <blockquote className="mt-3 rounded-xl border-l-4 border-primary bg-muted px-4 py-3">
            {CONSENT_TEXT}
          </blockquote>
          <p className="mt-3 text-sm text-muted-foreground">
            The button below it reads “{JOIN_BUTTON}”, or “{REQUEST_BUTTON}” on a private event.
          </p>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold">The messages</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every message is a single text (one SMS segment). Samples use the event “House move”.
          </p>
          <ul className="mt-4 space-y-4">
            {messages.map((m) => (
              <li key={m.when}>
                <p className="mb-1.5 text-sm font-medium">{m.when}</p>
                <p className="rounded-2xl rounded-bl-md bg-secondary px-4 py-2.5 text-sm break-words whitespace-pre-wrap">
                  {m.text}
                </p>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="space-y-2">
          <h2 className="text-xl font-semibold">Opting out</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              Reply STOP to any text to stop all texts from {APP_NAME}. Reply START to resume.
            </li>
            <li>Reply HELP for help, or email {ctx.support}.</li>
            <li>Every updates page has “Leave this event” and “Never add me to events”.</li>
            <li>Every email has a one-click unsubscribe link.</li>
          </ul>
          <p className="pt-2">
            Message frequency varies. Msg & data rates may apply. Mobile numbers, email addresses
            and opt-in consent are never shared with or sold to third parties or affiliates for
            marketing purposes. See our{' '}
            <Link to="/terms" className="underline">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline">
              Privacy Policy
            </Link>
            .
          </p>
        </Card>
      </article>
      <Footer texts />
    </Page>
  )
}
